import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

/**
 * Global Staking status. While `live` is false, users can browse plans, but
 * subscription entry points render a locked "Coming Soon" state and stake
 * creation is rejected by the backend.
 */
export function useStakingStatus() {
  const { data, isLoading } = useQuery({
    queryKey: ["platform_settings", "staking"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_public_platform_settings");
      if (error) throw error;
      return (data ?? null) as any;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const live = (data?.staking_live as boolean) ?? false;
  return { live, locked: !live, loading: isLoading, settingsId: data?.id as string | undefined };
}

export function useSetStakingLive() {
  const qc = useQueryClient();
  const { settingsId } = usePlatformSettings();

  return useMutation({
    mutationFn: async (live: boolean) => {
      if (!settingsId) throw new Error("Platform settings not initialised");
      const { error } = await supabase
        .from("platform_settings" as any)
        .update({ staking_live: live } as any)
        .eq("id", settingsId);
      if (error) throw error;
      return live;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform_settings"] });
      qc.invalidateQueries({ queryKey: ["platform_settings", "staking"] });
    },
  });
}
